if (!customElements.get('sembonia-image-viewer')) {
  customElements.define('sembonia-image-viewer', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('.sembonia-image-viewer__track');
      this.slides = Array.from(this.querySelectorAll('[data-viewer-media-id]'));
      this.allSlides = this.slides;
      this.index = 0;
      this.zoomButton = this.querySelector('.sembonia-image-viewer__zoom');
      this.zoomPane = this.querySelector('.sembonia-image-viewer__zoom-pane');
      this.zoomButton?.addEventListener('click', () => this.setZoom(!this.zoomed));
      this.bindImageZoom();
      this.track.addEventListener('load', () => this.updateZoomButton(), true);
      this.querySelector('.sembonia-image-viewer__close').addEventListener('click', () => this.hide());
      this.querySelector('.sembonia-image-viewer__previous')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('.sembonia-image-viewer__next')?.addEventListener('click', () => this.go(this.index + 1));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.hide();
        if (event.target.closest('video, model-viewer, input, textarea')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.track.addEventListener('scroll', () => {
        if (!this.hasAttribute('open') || !this.track.clientWidth) return;
        this.update(Math.round(this.track.scrollLeft / this.track.clientWidth));
      }, { passive: true });
      this.resizeObserver = new ResizeObserver(() => {
        if (this.hasAttribute('open')) this.go(this.index, false);
      });
      this.resizeObserver.observe(this.track);
    }
    show(opener) {
      this.openedBy = opener;
      // Match the visible PDP gallery, not every hidden size/colour media copy.
      const gallery = opener?.closest('media-gallery');
      if (gallery) {
        const visibleIds = Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]'))
          .filter(item => item.getClientRects().length > 0)
          .map(item => item.dataset.mediaId.split('-').pop());
        const requestedId = opener.getAttribute('data-media-id');
        this.allSlides.forEach(slide => {
          slide.hidden = !visibleIds.includes(slide.dataset.viewerMediaId) && slide.dataset.viewerMediaId !== requestedId;
        });
        this.slides = this.allSlides.filter(slide => !slide.hidden);
      }
      // Move out of the sticky/translated PDP ancestors before positioning fullscreen.
      if (this.parentElement !== document.body) document.body.appendChild(this);
      this.resizeObserver.observe(this.track);
      window.semboniaScrollLock.acquire(this);
      this.setAttribute('open', '');
      window.pauseAllMedia();
      const requested = this.slides.findIndex(slide => slide.dataset.viewerMediaId === opener?.getAttribute('data-media-id'));
      this.go(requested < 0 ? 0 : requested, false);
      trapFocus(this, this.querySelector('.sembonia-image-viewer__close'));
    }
    update(index) {
      const next = Math.max(0, Math.min(index, this.slides.length - 1));
      if (next !== this.index) window.pauseAllMedia();
      this.index = next;
      this.updateZoomButton();
      this.slides.forEach((slide, i) => { slide.inert = i !== next; });
      this.querySelector('.sembonia-image-viewer__status').textContent = `${next + 1} / ${this.slides.length}`;
    }
    go(index, smooth = true) {
      if (!this.slides.length) return;
      this.setZoom(false);
      const next = (index + this.slides.length) % this.slides.length;
      this.update(next);
      this.track.scrollTo({ left: next * this.track.clientWidth, top: 0, behavior: smooth && !window.semboniaMobileBaseline?.active ? 'smooth' : 'instant' });
    }
    hide() {
      if (!this.hasAttribute('open')) return;
      this.setZoom(false);
      this.removeAttribute('open');
      window.pauseAllMedia();
      window.semboniaScrollLock.release(this);
      removeTrapFocus(this.openedBy);
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
    }
    bindImageZoom() {
      const desktop = () => window.matchMedia('(min-width: 1200px) and (hover: hover)').matches;
      this.addEventListener('click', event => {
        if (event.target.closest('.sembonia-image-viewer__track img, .sembonia-image-viewer__zoom-pane img') && desktop() && Date.now() - (this.lastTouch || 0) > 600) this.setZoom(!this.zoomed);
      });
      this.addEventListener('dblclick', event => {
        if (!desktop() && Date.now() - (this.lastTouch || 0) > 600 && event.target.closest('.sembonia-image-viewer__track img, .sembonia-image-viewer__zoom-pane img')) {
          event.preventDefault(); this.setZoom(!this.zoomed);
        }
      });
      const distance = touches => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
      this.addEventListener('touchstart', event => {
        if (!event.target.closest('.sembonia-image-viewer__track, .sembonia-image-viewer__zoom-pane')) return;
        this.lastTouch = Date.now();
        if (event.touches.length === 2) {
          this.pinchStart = distance(event.touches); this.pinchRatio = 1; this.tapStart = null;
        } else if (event.touches.length === 1) {
          this.tapStart = {x:event.touches[0].clientX,y:event.touches[0].clientY,time:Date.now()};
        }
      }, {passive:true});
      this.addEventListener('touchmove', event => {
        if (this.pinchStart && event.touches.length === 2) {
          event.preventDefault(); this.pinchRatio = distance(event.touches) / this.pinchStart;
        } else if (this.tapStart && event.touches.length && Math.hypot(event.touches[0].clientX-this.tapStart.x,event.touches[0].clientY-this.tapStart.y)>12) this.tapStart=null;
      }, {passive:false});
      this.addEventListener('touchend', event => {
        this.lastTouch = Date.now();
        if (this.pinchStart) {
          if (event.touches.length < 2) {
            if(this.pinchRatio > 1.12) this.setZoom(true);
            if(this.pinchRatio < 0.88) this.setZoom(false);
            this.pinchStart=0; this.tapStart=null; this.lastTap=null;
          }
          return;
        }
        const tap=this.tapStart; this.tapStart=null;
        if (!tap || event.touches.length || Date.now()-tap.time>300) return;
        if(this.lastTap && tap.time-this.lastTap.time<350 && Math.hypot(tap.x-this.lastTap.x,tap.y-this.lastTap.y)<30) {
          event.preventDefault(); this.setZoom(!this.zoomed); this.lastTap=null;
        } else this.lastTap=tap;
      }, {passive:false});
      this.addEventListener('touchcancel', () => { this.tapStart=null; this.pinchStart=0; this.lastTap=null; });
    }
    updateZoomButton() {
      if (!this.zoomButton) return;
      const image = this.slides[this.index]?.querySelector('.sembonia-image-viewer__image');
      this.zoomButton.hidden = !image;
      this.zoomButton.disabled = !!image && !image.naturalWidth;
    }
    setZoom(enabled) {
      if (!this.zoomPane) return;
      const image = this.slides[this.index]?.querySelector('.sembonia-image-viewer__image');
      if (enabled && (!image || !image.naturalWidth)) return;
      this.zoomed = enabled;
      this.zoomPane.hidden = !enabled;
      this.track.inert = enabled;
      this.zoomButton.setAttribute('aria-pressed', String(enabled));
      this.zoomButton.setAttribute('aria-label', enabled ? 'Zoom out' : 'Zoom in');
      this.zoomPane.replaceChildren();
      if (!enabled) return;
      const enlarged = image.cloneNode();
      enlarged.removeAttribute('class');
      const ratio = image.naturalWidth / image.naturalHeight;
      const fitWidth = Math.min(this.track.clientWidth, this.track.clientHeight * ratio);
      enlarged.style.width = `${Math.max(fitWidth * 2, this.track.clientWidth)}px`;
      enlarged.style.minHeight = '0';
      this.zoomPane.appendChild(enlarged);
      this.zoomPane.scrollTo({left: (this.zoomPane.scrollWidth - this.zoomPane.clientWidth) / 2,
        top: (this.zoomPane.scrollHeight - this.zoomPane.clientHeight) / 2, behavior: 'instant'});
    }
    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.semboniaScrollLock.release(this);
    }
  });
}
